import React, { useEffect, useState } from "react";
import { SaveOutlined, ReloadOutlined } from "@ant-design/icons";
import { Checkbox, Card, Radio, Select } from "antd";
import { INVENTORY_TYPES, INVOICE_NAME_AND_LOGO_SETTINGS } from "../../constant/constants";
import {
  defaultSettingsData,
  getRestaurantSettingsData,
  getUpdatedGlobalStateRestaurants,
  tableManagementOptions,
} from "../../services/super-admin/settings-super-admin.service";
import useUserStore from "../../stores/store";
import { updateProfileSettings } from "../../services/profile-service";

/**
 * This component should be used other places and everything should be done inside it or its child component but recommended not pass any data to its invoked page or component
 *
 * @param {*} isInvoice
 * @returns
 */
export default function Settings({ parentRestaurantList }) {
  const { user, branches, setUser, showNotification, setNetworkCall } = useUserStore();
  const [branchDetailLoader, setBranchDetailLoader] = useState(false);
  const [restaurantList, setRestaurantList] = useState([]);
  const [parentRestaurantId, setParentRestaurantId] = useState("");
  const [inputData, setInputData] = useState({ ...defaultSettingsData });

  const updateGlobalStateRestaurant = () => {
    const updatedData = getUpdatedGlobalStateRestaurants(
      inputData.tableManagement,
      inputData.inventoryType,
      inputData.invoiceSettingsNameAndLogo,
      inputData.restaurantId,
      branches,
      user.restaurants
    );

    setUser({ ...user, restaurants: updatedData });
  };

  const onHanldeReset = () => {
    setParentRestaurantId("");
    setInputData({ ...defaultSettingsData });
  };

  const onHanldeSave = async () => {
    if (!inputData.inventoryType) {
      showNotification("Inventory Type is required.");
      return;
    }

    if (!inputData.invoiceSettingsNameAndLogo?.length) {
      showNotification("Invoice settings is required.");
      return;
    }

    setNetworkCall(true);
    const result = await updateProfileSettings(inputData, user.token.token);

    console.log('-----result---', {result});

    setNetworkCall(false);

    if (result.success) {
      console.log('-------------success------------');
      showNotification(result.msg, false);
      await new Promise(r => setTimeout(r, 300)); // This is needed as before showing notification global state will be reset
      updateGlobalStateRestaurant();
    } else {
      console.log('-----------------fail--------------');
      showNotification(result.msg);
    }
  };

  const onHandleInventoryTypeChange = (e) => {
    setInputData((prev) => ({
      ...prev,
      inventoryType: e.target.value,
    }));
  };

  const onHandleInvoiceSettingsChange = (value) => {
    setInputData((prev) => ({
      ...prev,
      invoiceSettingsNameAndLogo: value,
    }));
  };

  const onSelectParentRestaurant = (val) => {
    setParentRestaurantId(val);
    const parentItem = parentRestaurantList.find((item) => item.value === val);
    setRestaurantList(parentItem.branches);

    setInputData((prev) => ({
      ...prev,
      ...defaultSettingsData,
    }));
  };

  const onSelectRestaurant = async (val) => {
    if (!val) {
      setInputData((prev) => ({
        ...prev,
        ...defaultSettingsData,
      }));

      return;
    }

    setInputData((prev) => ({
      ...prev,
      restaurantId: val,
    }));

    setBranchDetailLoader(true);

    const result = await getRestaurantSettingsData(val, user.token.token);

    console.log("--------result--get settings data-----", { result });

    setBranchDetailLoader(false);

    if (!result.success) {
      showNotification(result.message);
      return;
    }

    setInputData((prev) => ({
      ...prev,
      ...result.settingsData,
      restaurantId: val,
    }));
  };

  return (
    <div>
      <div className="flex flex-row gap-6 mb-[20px]">
        {/* Restaurant List */}
        <div className="flex flex-col flex-1">
          <div className="ml-[4px] font-uregular flex flex-row">
            <div className="mb-1 font-normal">Restaurant List</div>
            <div className="font-ubold text-red-700 ml-[2px]">*</div>
          </div>

          {parentRestaurantList.length > 0 && (
            <Select
              className="font-uregular w-full"
              showSearch
              value={parentRestaurantId}
              placeholder="Select restaurant name"
              onChange={onSelectParentRestaurant}
              filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
              options={parentRestaurantList}
            />
          )}
        </div>

        {/* Branch List */}
        <div className="flex flex-col flex-1">
          <div className="ml-[4px] font-uregular flex flex-row">
            <div className="mb-1 font-normal">Branch List</div>
            <div className="font-ubold text-red-700 ml-[2px]">*</div>
          </div>

          <Select
            className="font-uregular w-full"
            value={inputData.restaurantId}
            placeholder="Select branch"
            onChange={onSelectRestaurant}
            options={restaurantList}
          />
        </div>
      </div>

      {branchDetailLoader ? (
        <div className="mb-[12px] mt-2 p-2">Loading existing settings...</div>
      ) : (
        <div className="flex flex-col md:flex-row gap-6 mb-[20px] items-stretch">
          {/* Inventory Type Card */}
          <Card
            title={
              <div className="font-ubold flex items-center">
                Inventory Settings
                <span className="text-red-700 ml-[4px]">*</span>
              </div>
            }
            className="flex-1 flex flex-col justify-between shadow-md"
            styles={{ body: { display: "flex", flexDirection: "column", height: "100%" } }}
          >
            <div className="flex flex-col items-start gap-3">
              <Radio.Group
                options={INVENTORY_TYPES}
                value={inputData.inventoryType}
                onChange={onHandleInventoryTypeChange}
                className="flex gap-4 flex-wrap"
              />
            </div>
          </Card>

          {/* Invoice Settings Card */}
          <Card
            title={
              <div className="font-ubold flex items-center">
                Invoice Settings (Name & Logo)
                <span className="text-red-700 ml-[4px]">*</span>
              </div>
            }
            className="flex-1 flex flex-col justify-between shadow-md"
            styles={{ body: { display: "flex", flexDirection: "column", height: "100%" } }}
          >
            <div className="flex flex-col items-start gap-3">
              <Checkbox.Group
                options={INVOICE_NAME_AND_LOGO_SETTINGS}
                value={inputData.invoiceSettingsNameAndLogo}
                onChange={onHandleInvoiceSettingsChange}
                className="flex gap-4 flex-wrap"
              />
            </div>
          </Card>
            {/* Table Management Settings Card */}
    <Card
      title={
        <div className="font-ubold flex items-center">
          Table Management Settings
          <span className="text-red-700 ml-[4px]">*</span>
        </div>
      }
      className="flex-1 flex flex-col justify-between shadow-md"
      styles={{ body: { display: "flex", flexDirection: "column", height: "100%" } }}
    >
      <div className="flex flex-col items-start gap-3">
        <Radio.Group
          value={inputData.tableManagement}
          onChange={(e) =>
            setInputData((prev) => ({
              ...prev,
              tableManagement: e.target.value,
            }))
          }
          options={tableManagementOptions}
        />
      </div>
    </Card>

        </div>
      )}

      <div className="w-[280px] flex m-auto justify-between pt-10 ">
        <button
          key="kitchenSlipOrInvoice1"
          className="bg-[#2EC114] text-[#FFFFFF] rounded-[5px] font-semibold w-[110px]"
          onClick={() => onHanldeSave()}
          style={{
            padding: "8px 16px",
          }}
        >
          <SaveOutlined style={{ marginRight: 6 }} />
          Save
        </button>
        <button
          key="kitchenSlipOrInvoice2"
          className="bg-[#FCBD10] text-[#FFFFFF] rounded-[5px] font-semibold w-[110px]"
          onClick={() => onHanldeReset()}
          style={{
            padding: "8px 16px",
          }}
        >
          <ReloadOutlined style={{ marginRight: 6 }} />
          Clear
        </button>
      </div>
    </div>
  );
}
